import dayjs from 'dayjs';

// For Min Date On DateBox
export const minDate: Date = new Date(1901, 0, 1);
export const minDateFormated = dayjs(new Date(1901, 0, 1)).format("MM/DD/YYYY");
export const maxDate: Date = new Date();
export const dateOutOfRangeMessage: string = "Invalid date";
const monthNamesShort = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];


// For Date Column Sorting
export const sortingMethodDate = (v1: string | Date | null, v2: string | Date | null): number => {
  const value1 = v1 ? new Date(v1) : null;
  const value2 = v2 ? new Date(v2) : null;

  if (!value1 && value2) return -1;
  if (!value1 && !value2) return 0;
  if (value1 && !value2) return 1;
  if (value1?.getTime() === value2?.getTime()) return 0;
  return (value1 && value2 && value1 > value2) ? 1 : -1;
};

export function GetFormattedDateOnly(dateString?: string | Date | null): string | null {
  if (dateString) {
    const utcDate = new Date(dateString);
    const localDate = new Date(utcDate.getTime() + utcDate.getTimezoneOffset() * 60000);
    return `${monthNamesShort[localDate.getMonth()]} ${localDate.getDate()}, ${localDate.getFullYear()}`;
  }
  return null;
}

export const GetFormattedDateTime = (dateString?: string | Date | null): string | null => {
  if (dateString) {
    const localDate = new Date(dateString);

    const month = monthNamesShort[localDate.getMonth()];
    const day = localDate.getDate();
    const year = localDate.getFullYear();

    let hours = localDate.getHours();
    const minutes = pad(localDate.getMinutes());
    const seconds = pad(localDate.getSeconds());
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;

    return `${month} ${day}, ${year}, ${hours}:${minutes}:${seconds} ${ampm}`;
  }
  return null;
};

const pad = (num: number): string => num < 10 ? "0" + num : num.toString();

export const GetFormattedDateTimeUTCString = (dateString?: string | Date | null): string | null => {
  if (dateString) {
    const utcDate = new Date(dateString);

    const month = monthNamesShort[utcDate.getMonth()];
    const day = utcDate.getDate();
    const year = utcDate.getFullYear();

    let hours = utcDate.getUTCHours();
    const minutes = pad(utcDate.getUTCMinutes());
    const seconds = pad(utcDate.getUTCSeconds());
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12 || 12;

    return `${month} ${day}, ${year}, ${hours}:${minutes}:${seconds} ${ampm}`;
  }
  return null;
};

export const GetFormattedCustomDateOnly = (dateString?: string | Date | null): string | null => {
  if (dateString) {
    const date = new Date(dateString);
    return `${monthNamesShort[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  }
  return null;
};


export const formatDateTo = (date: string | Date): string => {
  const d = new Date(date);
  let month = "" + (d.getMonth() + 1);
  let day = "" + d.getDate();
  const year = d.getFullYear();

  if (month.length < 2) month = "0" + month;
  if (day.length < 2) day = "0" + day;

  return [year, month, day].join("-");
};

export const GetFormattedDateTimeUTC = (dateTimeValue?: string | null): string | null => {
  const DateTimeValue = dateTimeValue ? dateTimeValue.split("T") : null;
  if (DateTimeValue) {
    const DateValue = DateTimeValue[0];
    const TimeValue = formatTimeTo(DateTimeValue[1]);
    const dueDateTime = GetFormattedDate(DateValue) + " " + TimeValue;
    return dateTimeValue ? dueDateTime : null;
  }
  return null;
};

export const formatTimeTo = (timeString: string): string => {
  const [hourString, minute] = timeString.split(":");
  const hour = +hourString % 24;
  return (hour % 12 || 12) + ":" + minute + (hour < 12 ? " AM" : " PM");
};

export function GetFormattedDate(newDate?: string | Date | null): string | null {
  if (newDate) {
    const updatedDate = new Date(newDate);
    if (isNaN(updatedDate.getTime())) return null;
    const date = new Date(updatedDate.toISOString().split("T")[0] + "T00:00-0800");
    return `${monthNamesShort[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  }
  return null;
}

export function GetFormattedTimeOnly(newDateTime: string | Date): string {
  const date = new Date(newDateTime);
  const hours = date.getHours();
  const minutes = date.getMinutes();

  const formattedHours = ((hours + 11) % 12) + 1;
  const formattedMinutes = minutes.toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";

  return `${formattedHours}:${formattedMinutes} ${ampm}`;
}



// Dynamically create columns from data source 
export function formatToMonthYear(isoString: string) {
  const date = new Date(isoString);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${year}`;
}

// If you want a filter in datagrid but with lookupoptions (Example in pages/inventory/products/list)
export function lookupDataToFilterOptions<T extends object>(
  data: T[],
  valueKey: keyof T,
  labelKey: keyof T
) {
  const options = data?.map((item) => ({
    value: String(item[valueKey]),
    label: String(item[labelKey])
  }));

  // Add the "All" option at the start
  return [{ value: 'all', label: 'All' }, ...options];
}


// Common validation for Decimal(18,4)
export const validateDecimalInput = (
  val: string,
  format?: string,
  allowNegative: boolean = false
): boolean => {
  // Allow empty string
  if (val === '') return true;

  // Allow only digits, one decimal point, and optionally a leading minus sign
  const regex = allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;
  if (!regex.test(val)) return false;

  // Parse format if provided (precision, scale)
  let precision = 18; // default max total digits
  let scale = 4;      // default max decimals
  if (format) {
    const [p, s] = format.split(',').map(Number);
    if (!isNaN(p)) precision = p;
    if (!isNaN(s)) scale = s;
  }

  // Remove negative sign for length validation
  const valueToValidate = val.startsWith('-') ? val.slice(1) : val;

  // Split into integer and decimal parts
  const [intPart, decPart = ''] = valueToValidate.split('.');

  // Validate integer and decimal parts based on precision/scale
  if (decPart.length > scale) return false; // too many decimal places
  if ((intPart.length + decPart.length) > precision) return false; // exceeds precision

  return true;
};


export const formatDecimal = (num: number) => {
  if (Number.isInteger(num)) return num.toString();
  return parseFloat(num.toFixed(4)).toString(); // max 4 decimals
};


export const formatCurrency = (value: number) => {
  if (isNaN(value) || value === null || value === undefined)
    return "$0";
  return `$${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
};


interface CurrencyOptions {
  currencyName?: string;      // e.g., Dollars, Rupees
  subCurrencyName?: string;   // e.g., Cents, Paise
  onlyText?: string;          // default "Only"
};

export function numberToWordsUniversal(
  amount: number | string,
  options?: CurrencyOptions
): string {
  if (amount === null || amount === undefined || amount === '') return '';

  const num = Number(amount);
  if (isNaN(num)) return '';

  const ones = [
    '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six',
    'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve',
    'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
    'Seventeen', 'Eighteen', 'Nineteen'
  ];

  const tens = [
    '', '', 'Twenty', 'Thirty', 'Forty',
    'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'
  ];

  const convert = (n: number): string => {
    if (n < 20) return ones[n];
    if (n < 100)
      return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    if (n < 1000)
      return (
        ones[Math.floor(n / 100)] +
        ' Hundred' +
        (n % 100 ? ' ' + convert(n % 100) : '')
      );
    if (n < 1000000)
      return (
        convert(Math.floor(n / 1000)) +
        ' Thousand' +
        (n % 1000 ? ' ' + convert(n % 1000) : '')
      );
    if (n < 1000000000)
      return (
        convert(Math.floor(n / 1000000)) +
        ' Million' +
        (n % 1000000 ? ' ' + convert(n % 1000000) : '')
      );

    return (
      convert(Math.floor(n / 1000000000)) +
      ' Billion' +
      (n % 1000000000 ? ' ' + convert(n % 1000000000) : '')
    );
  };

  const integerPart = Math.floor(num);
  const decimalPart = Math.round((num - integerPart) * 100);

  // ✅ NUMBER ONLY MODE
  if (!options?.currencyName) {
    return convert(integerPart).trim();
  }

  // ✅ CURRENCY MODE
  let words = `${convert(integerPart)} ${options.currencyName}`;

  if (decimalPart > 0 && options.subCurrencyName) {
    words += ` and ${convert(decimalPart)} ${options.subCurrencyName}`;
  }

  return `${words} ${options.onlyText ?? 'Only'}`.replace(/\s+/g, ' ').trim();
}


export const getBase64FromUrl = async (url: string): Promise<string> => {
  if (!url) return '';
  try {
    const res = await fetch(url);
    if (!res.ok) return '';
    const blob = await res.blob();

    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;

      reader.readAsDataURL(blob);
    });
    const fixedBase64 = base64.replace(
      "data:application/octet-stream",
      "data:image/jpeg"
    );
    return fixedBase64;
  } catch (error) {
    console.error('Error fetching image for PDF:', error);
    return '';
  }
};

export const formatCurrencyInput = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === "") return "";

  const stringValue = String(value);

  const cleaned = stringValue.replace(/,/g, "");

  if (!/^\d*\.?\d*$/.test(cleaned)) return stringValue;

  const [integerPart, decimalPart] = cleaned.split(".");

  const formattedInteger = Number(integerPart || 0).toLocaleString("en-US");

  return decimalPart !== undefined
    ? `${formattedInteger}.${decimalPart}`
    : formattedInteger;
};

export const GetFriendlyTaskDueDate = (dateString?: string | Date | null): string => {
  if (!dateString) return '';
  // Strip timezone indicators (Z or ±HH:mm) so dayjs parses the value as-is
  // without any UTC-to-local conversion (e.g. "2026-07-29T15:45:00Z" → "2026-07-29T15:45:00")
  const rawStr =
    typeof dateString === 'string'
      ? dateString.replace(/Z$/i, '').replace(/[+-]\d{2}:\d{2}$/, '')
      : dateString;
  const d = dayjs(rawStr);
  if (!d.isValid()) return String(dateString);

  const now = dayjs();
  const startOfToday = now.startOf('day');
  const startOfDate = d.startOf('day');
  
  const diffDays = startOfDate.diff(startOfToday, 'day');
  const timeStr = d.format('h:mm A');

  if (diffDays === 0) {
    return `Today • ${timeStr}`;
  } else if (diffDays === -1) {
    return `Yesterday • ${timeStr}`;
  } else if (diffDays === 1) {
    return `Tomorrow • ${timeStr}`;
  } else if (diffDays > 1 && diffDays < 7) {
    return `${d.format('dddd')} • ${timeStr}`;
  } else {
    return `${d.format('MMM DD, YYYY')} • ${timeStr}`;
  }
};

export const stripAndDecodeHtml = (htmlString?: string | null): string => {
  if (!htmlString) return '';
  try {
    const doc = new DOMParser().parseFromString(htmlString, 'text/html');
    return (doc.body.textContent || '').trim();
  } catch {
    return htmlString
      .replace(/<[^>]+>/g, '')
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&nbsp;/g, ' ')
      .trim();
  }
};

export const cleanNoteHtml = (html?: string | null): string => {
  if (!html) return '';
  return html
    .replace(/(<p>\s*(<br\s*\/?>|&nbsp;|\s*)*<\/p>|<br\s*\/?>\s*)+$/gi, '')
    .replace(/^(<p>\s*(<br\s*\/?>|&nbsp;|\s*)*<\/p>|<br\s*\/?>\s*)+/gi, '')
    .trim();
};